import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserRoles } from '../constants';
import { useAppContext } from '../Store';
import { notify, userHasRole } from '../utils/helper';
import './DocumentList.css';
import DocumentResultCard from './DocumentResultCard';
import useOutsideAlerter from '../hooks/useOutsideAlerter';
import { SET_PREVIOUS_VIEWDOC_ID, SHOW_ALL_RESULT_CARD_DETAILS } from '../reducer';
import { useGetSearchResults_V2 } from '../services/getSearchResults_V2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import DownloadIcon from '@mui/icons-material/Download';
import { exportSearchResults } from '../services';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const DocumentList = () => {
  const { state, dispatch } = useAppContext();
  const { search } = state;
  const { allExpanded } = search;
  const [height, setHeight] = useState<number>(0);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const headerRef = useRef<null | HTMLDivElement>(null);
  const wrapperRef = useRef<null | HTMLDivElement>(null);
  const { isOutsideClick } = useOutsideAlerter(wrapperRef);
  const firstResultRef = useRef<HTMLDivElement>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clickDelay = 250;

  const {
    data: searchData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetSearchResults_V2(state.search);

  const hits = useMemo(() => searchData?.pages.flatMap((page) => page.hits), [searchData]);
  const ids = useMemo(() => hits?.map((h) => h._id), [hits]);
  const groupCount = useMemo(() => searchData?.pages?.[0]?.groupCount, [searchData]);
  const totalCount = useMemo(() => searchData?.pages?.[0]?.count ?? 0, [searchData]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 1.0,
    });

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [handleObserver]);

  const handleSingleClick = () => {
    if (clickTimer.current) return;
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      const y =
        (firstResultRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY - height;
      window.scrollTo({ top: y, behavior: 'smooth' });
      dispatch({ type: SET_PREVIOUS_VIEWDOC_ID, payload: null });
    }, clickDelay);
  };

  const handleDoubleClick = () => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    dispatch({ type: SET_PREVIOUS_VIEWDOC_ID, payload: null });
  };

  useEffect(() => {
    const handleResize = () => {
      setHeight(headerRef.current?.offsetHeight || 0);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (allExpanded)
      dispatch({ type: SHOW_ALL_RESULT_CARD_DETAILS, payload: { ids, allExpanded } });
  }, [ids, allExpanded, dispatch]);

  const isEditor = userHasRole(
    [UserRoles.MANAGER, UserRoles.ADMIN],
    state.auth.roles as UserRoles[]
  );

  return (
    <div style={{ backgroundColor: '#ecf1f9', flex: 1 }}>
      <div className="listing-card-row__set">
        <div
          style={{
            textAlign: 'center',
            fontWeight: 'bold',
            position: 'relative',
            top: '-30px',
            height: '10px',
          }}
        >
          {isLoading ? (
            <FontAwesomeIcon icon={faSpinner} />
          ) : groupCount ? (
            `${groupCount > 10000 ? '10000+' : groupCount} hits`
          ) : (
            ''
          )}
          {groupCount === 0 && !hasNextPage && (
            <p className="body-text--16 text-align-center">
              No results found. Please modify your search query or the filters.
            </p>
          )}
        </div>
        <div
          ref={headerRef}
          style={{
            height: '58px',
            backgroundColor: 'rgb(236, 241, 249)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        ></div>
        {!isLoading && groupCount !== 0 && (
          <>
            <div
              className="listing-card-header"
              onClick={handleSingleClick}
              onDoubleClick={handleDoubleClick}
            >
              <Stack
                display={'flex'}
                direction={'row'}
                width={'100%'}
                alignItems={'center'}
                title="Single Click scrolls to Results, Double click to Top of the Page"
              >
                <Typography fontWeight={700} width={'20%'}>
                  <u>Form Number</u>
                </Typography>
                <Typography fontWeight={700} width={'60%'}>
                  <u>Form Title</u>
                </Typography>
                <Stack direction={'row'} width={'20%'} alignItems={'center'}>
                  <Typography fontWeight={700}>
                    <u>Form Type</u>
                  </Typography>
                </Stack>
              </Stack>
              <Tooltip
                title={allExpanded ? 'Collapse All' : 'Expand All'}
                placement="top-start"
                style={{ position: 'relative', width: 0 }}
              >
                <span>
                  <IconButton
                    size="medium"
                    sx={{
                      width: 40,
                      height: 40,
                    }}
                    style={{ marginLeft: 'auto', marginRight: '8px', right: '48px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({
                        type: SHOW_ALL_RESULT_CARD_DETAILS,
                        payload: { ids, allExpanded: !allExpanded },
                      });
                    }}
                  >
                    {allExpanded ? (
                      <ExpandLessIcon fontSize="large" />
                    ) : (
                      <ExpandMoreIcon fontSize="large" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {totalCount !== 0 && (
                <Tooltip
                  title="Download Results CSV"
                  placement="top"
                  style={{ position: 'relative', right: '8px', width: 0 }}
                >
                  <span>
                    <IconButton
                      aria-label="Download Results CSV"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (totalCount < 1000) {
                          setIsExportLoading(true);
                          await exportSearchResults(
                            state.search.searchQuery,
                            state,
                            totalCount,
                            state.search.narrow
                          );
                          setIsExportLoading(false);
                        } else {
                          notify(
                            'If you would like to download your results, please refine your results with filter',
                            'info'
                          );
                        }
                      }}
                    >
                      {isExportLoading ? <FontAwesomeIcon icon={faSpinner} /> : <DownloadIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>

            <Stack flex={1} ref={wrapperRef}>
              {height !== 0 &&
                hits?.map((doc, idx) => (
                  <div ref={idx === 0 ? firstResultRef : undefined} key={`${idx}-${doc._id}`}>
                    <DocumentResultCard
                      doc={doc}
                      isEditor={isEditor}
                      disableCardAnchor={isOutsideClick}
                    />
                  </div>
                ))}
              <div
                ref={loadMoreRef}
                style={{ textAlign: 'center', fontWeight: 500, paddingTop: '1rem' }}
              >
                {hasNextPage && 'Loading...'}
              </div>
            </Stack>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentList;
